import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useParams, useRouter } from 'next/navigation';
import { checkoutFlow } from '@commercetools/checkout-browser-sdk';
import toast from 'react-hot-toast';
import { AccountContext } from 'context/account';
import useCountry from 'helpers/hooks/useCountry';
import { useFormat } from 'helpers/hooks/useFormat';
import useLanguage from 'helpers/hooks/useLanguage';
import { pushDataLayerEvent } from 'helpers/utils/datalayer';
import { sendBeginCheckoutEvent } from 'helpers/utils/ga4';
import { CF_REGEX } from 'helpers/validation';
import { getCountryFromCode } from 'project.config';
import countryStates from 'static/states.json';
import { useCart, useCheckout, useProjectSettings } from 'frontastic';
import { ShippingMethod } from './ShippingSelection';

interface CtCheckoutFormProps {
  shippingMethod: ShippingMethod;
}

const CtCheckoutForm: React.FC<CtCheckoutFormProps> = ({}) => {
  const { push: pushRoute, replace: replaceRoute } = useRouter();
  const { locale } = useParams();

  const { formatMessage } = useFormat({ name: 'gruenland' });

  const initiatedCheckout = useRef(false);
  const errorTriggered = useRef(false);

  const [isLoading, setIsLoading] = useState(true);

  const { data: projectSettings } = useProjectSettings();
  const { projectKey } = projectSettings ?? {};

  const [language] = useLanguage();
  const [country] = useCountry();
  const checkoutLocale = language.slice(0, 2);

  const { session, isExpired, isInvalidCountry } = useCheckout();

  const { account, logout } = useContext(AccountContext);

  const { data } = useCart();

  useEffect(() => {
    if (initiatedCheckout.current || !projectKey || !session?.token) {
      return;
    }

    initiatedCheckout.current = true;

    const provinces = countryStates[country.countryCode as keyof typeof countryStates] ?? [];
    const provinceField = {
      label: formatMessage({ id: 'address.province', defaultMessage: 'Provincia' }),
      customType: 'String' as const,
      ...(provinces.length > 0
        ? {
            componentType: 'select' as const,
            valueOptions: provinces.map((x) => ({ value: x.code, label: x.name })),
            validation: {
              required: {
                message: formatMessage({
                  id: 'validation.required',
                  defaultMessage: 'Campo obbligatorio',
                }),
                value: true,
              },
            },
          }
        : {}),
    };
    const fiscalCodeField = country.hasCheckoutCfVat
      ? {
          label: formatMessage({ id: 'address.fiscalCode', defaultMessage: 'Codice fiscale' }),
          customType: 'String' as const,
          validation: {
            required: {
              message: formatMessage({
                id: 'validation.required',
                defaultMessage: 'Campo obbligatorio',
              }),
              value: true,
            },
            pattern: {
              value: CF_REGEX.toString(),
            },
          },
        }
      : undefined;
    const vatIdField = country.hasCheckoutCfVat
      ? {
          label: formatMessage({ id: 'address.vatId', defaultMessage: 'Partita IVA' }),
          customType: 'String' as const,
        }
      : undefined;

    checkoutFlow({
      region: process.env.NEXT_PUBLIC_COMMERCETOOLS_CHECKOUT_REGION || '',
      projectKey,
      sessionId: session.token,
      locale: checkoutLocale,
      styles: {
        '--font-family': "'Inter', sans-serif",
        '--button': '#212121',
        '--radio': '#343434',
        '--checkbox': '#343434',
      },
      languageOverrides: {
        delivery: {
          freeFrom: formatMessage({
            id: 'checkout.freeFrom',
            defaultMessage: 'Gratuita da',
          }),
        },
        shared: {
          optional: ' ', // Avoids showing "(optional)" in the UI
        },
        orderSummary: {
          discountCode: {
            invalid: {
              singular: formatMessage({
                id: 'error.maxUsageReached',
                defaultMessage:
                  'Questo codice sconto non può più essere utilizzato poiché è stato raggiunto il numero massimo di applicazioni. {codes}',
              }),
              plural: formatMessage({
                id: 'error.maxUsageReached',
                defaultMessage:
                  'Questo codice sconto non può più essere utilizzato poiché è stato raggiunto il numero massimo di applicazioni. {codes}',
              }),
            },
          },
        },
      },
      onInfo: (message) => {
        console.log('Checkout event:', message.code);
        switch (message.code) {
          case 'checkout_started':
            data && sendBeginCheckoutEvent(data);
            break;
          case 'checkout_cancelled':
            pushRoute('/cart');
            break;
          case 'checkout_loaded':
            setIsLoading(false);
            break;
          case 'checkout_completed':
            pushRoute(
              `${process.env.NEXT_PUBLIC_COMMERCETOOLS_CHECKOUT_CALLBACK_URL}?orderId=${
                (message.payload as any)?.order?.id //eslint-disable-line
              }`,
            );
            break;
          case 'order_created':
            console.log('Cart:', data);
            pushDataLayerEvent({
              event: 'purchase',
              ecommerce: {
                transaction_id: (message.payload as { order: { id: string } }).order.id,
                affiliation: 'Online store',
                currency: 'EUR',
                value: (data?.sum?.centAmount || 0) / 100,
              },
            });
            break;
        }
      },
      showBillingSameAsShipping: false,
      forms: {
        default: {
          billingAddress: {
            summaryLayout: [
              ['firstName', 'lastName'],
              ['streetName', 'streetNumber'],
              ['postalCode', 'city', 'province', 'country'],
              ['phone'],
              ...((country.hasCheckoutCfVat && [['fiscalCode']]) || []),
              ['company'],
              ...((country.hasCheckoutCfVat && [['vatId']]) || []),
            ],
            custom: {
              type: {
                key: 'addressCustomType',
              },
              fields: {
                province: provinceField,
                ...(fiscalCodeField && vatIdField
                  ? {
                      fiscalCode: fiscalCodeField,
                      vatId: vatIdField,
                    }
                  : {}),
              },
            },
            fields: {
              country: {
                validation: {
                  pattern: data?.country
                    ? {
                        value: `/^${data.country}\$/`,
                        message: formatMessage({
                          id: 'validation.country',
                          defaultMessage: 'La nazione deve essere {country}',
                          values: { country: getCountryFromCode(data.country)?.countryName || '' },
                        }),
                      }
                    : undefined,
                },
              },
            },
            layout: [
              ['firstName', 'lastName'],
              ['country'],
              ['streetName', 'streetNumber'],
              ['postalCode', 'city'],
              ['province'],
              ['phone'],
              ...((country.hasCheckoutCfVat && [['fiscalCode']]) || []),
              ['company'],
              ...((country.hasCheckoutCfVat && [['vatId']]) || []),
            ],
          },
          shippingAddress: data?.custom?.fields?.shippyProPickupPointId
            ? {
                // The pickup-point address (postalCode/city/streetName) is set on the cart by
                // setPickupPoint, but the commercetools Checkout SDK rewrites the shipping address
                // with ONLY the fields present in `layout` on submit, wiping everything else. We
                // must therefore keep these fields in the layout, prefilled from the pickup-point
                // custom fields, otherwise the cart shipping address loses its postalCode and the
                // generated PaymentIntent ships with an empty postal_code — which PayPal rejects
                // with POSTAL_CODE_REQUIRED. postalCode is locked to the pickup point value (same
                // pattern-lock used for country) since the user must not alter the pickup address.
                layout: [['firstName', 'lastName'], ['email'], ['phone'], ['postalCode', 'city'], ['streetName']],
                summaryLayout: [
                  ['firstName', 'lastName'],
                  ['email'],
                  ['phone'],
                  ['postalCode', 'city'],
                  ['streetName'],
                ],
                fields: {
                  country: {
                    validation: {
                      pattern: data?.country
                        ? {
                            value: `/^${data.country}\$/`,
                            message: formatMessage({
                              id: 'validation.country',
                              defaultMessage: 'La nazione deve essere {country}',
                              values: { country: getCountryFromCode(data.country)?.countryName || '' },
                            }),
                          }
                        : undefined,
                    },
                  },
                  phone: {
                    validation: {
                      required: {
                        value: true,
                      },
                    },
                  },
                  postalCode: {
                    label: formatMessage({ id: 'checkout.pudoPostalCode', defaultMessage: 'Punto di ritiro · CAP' }),
                    initialValue: data?.custom?.fields?.shippyProPickupPointZip || undefined,
                    validation: data?.custom?.fields?.shippyProPickupPointZip
                      ? {
                          pattern: {
                            value: `/^${data.custom.fields.shippyProPickupPointZip}\$/`,
                            message: formatMessage({
                              id: 'validation.pudoPostalCode',
                              defaultMessage: 'Il CAP deve corrispondere al punto di ritiro selezionato',
                            }),
                          },
                        }
                      : undefined,
                  },
                  city: {
                    label: formatMessage({ id: 'checkout.pudoCity', defaultMessage: 'Punto di ritiro · Località' }),
                    initialValue: data?.custom?.fields?.shippyProPickupPointCity || undefined,
                  },
                  streetName: {
                    label: formatMessage({ id: 'checkout.pudoStreet', defaultMessage: 'Punto di ritiro · Indirizzo' }),
                    initialValue: data?.custom?.fields?.shippyProPickupPointStreet || undefined,
                  },
                },
              }
            : {
                summaryLayout: [
                  ['firstName', 'lastName'],
                  ['email'],
                  ['streetName', 'streetNumber'],
                  ['postalCode', 'city', 'province', 'country'],
                  ['phone'],
                ],
                custom: {
                  type: {
                    key: 'addressCustomType',
                  },
                  fields: {
                    province: provinceField,
                  },
                },
                fields: {
                  country: {
                    validation: {
                      pattern: data?.country
                        ? {
                            value: `/^${data.country}\$/`,
                            message: formatMessage({
                              id: 'validation.country',
                              defaultMessage: 'La nazione deve essere {country}',
                              values: { country: getCountryFromCode(data.country)?.countryName || '' },
                            }),
                          }
                        : undefined,
                    },
                  },
                  phone: {
                    validation: {
                      required: {
                        value: true,
                      },
                    },
                  },
                },
                layout: [
                  ['firstName', 'lastName'],
                  ['email'],
                  ['country'],
                  ['streetName', 'streetNumber'],
                  ['postalCode', 'city'],
                  ['province'],
                  ['phone'],
                ],
              },
        },
      },
    });
  }, [projectKey, country, checkoutLocale, session, pushRoute, formatMessage, data]);

  useEffect(() => {
    if (isInvalidCountry) {
      replaceRoute('/cart/?error=invalidCountry');
    }
  }, [isInvalidCountry, replaceRoute]);

  useEffect(() => {
    if (account?.accountId && isExpired && !errorTriggered.current) {
      errorTriggered.current = true;
      logout().then(() => {
        pushRoute(`/${locale}/cart`);
        toast.error(formatMessage({ id: 'checkout.tokenExpired' }), { position: 'top-right' });
      });
    }
  }, [formatMessage, isExpired, pushRoute, locale, logout, account]);

  const handleGoingBack = useCallback(() => {
    const checkoutIframe = document.getElementById('ctc-wrapper');
    if (checkoutIframe) checkoutIframe.remove();
    window.onpopstate = null;
  }, []);

  useEffect(() => {
    window.onpopstate = handleGoingBack;
  }, [handleGoingBack]);

  return (
    <div className="relative pt-[6px]">
      {/* eslint-disable-next-line tailwindcss/no-custom-classname*/}
      <div data-ctc className="checkout-Container" />
      {isLoading && (
        <div className="flex h-[80vh] items-center justify-center">
          <div className="loading-full-screen" />
        </div>
      )}
    </div>
  );
};

export default CtCheckoutForm;
